import { format } from 'date-fns';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { authPaths } from '@/modules/auth/routes.tsx';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import UploadDocumentsBlock from '@/modules/common/components/UploadDocumentsBlock.tsx';
import { APPLY_REGISTER_ID } from '@/modules/common/constants/search-params.ts';
import { useQuantityPricePerShareTotal } from '@/modules/common/hooks/useQuantityPricePerShareTotal.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  useCurrentUserRegistersQuery,
  useRegisterClassListInfinityQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useIssuerCreateTransferMutation } from '@/modules/issuer-dashboard/queries/transfers.tsx';
import {
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { AlertIcon } from 'ui/icons';
import { copyTextToClipBoard, getDisableTime, getDisabledDate } from 'ui/utils';
import { TransferShareholderType } from 'api';

const validationSchema = yup.object({
  from_shareholder_id: yup
    .number()
    .typeError('Shareholder id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  to_shareholder_id: yup
    .number()
    .typeError('Register id must be a number')
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.Exist,
      then: schema => {
        return schema.moreThan(0, 'This field is required').required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  register_class_id: yup
    .number()
    .typeError('Security class id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .moreThan(0.099999999999999, 'Quantity must be greater than or equal to 0.1')
    .required('This field is required'),
  memo: yup.string().trim(),
  total: yup.number().typeError('Total must be a number').required('This field is required'),
  price_per_share: yup
    .number()
    .typeError('Total must be a number')
    .required('This field is required'),
  document_data: yup
    .array()
    .of(yup.string())
    .min(1, 'At least 1 document is required')
    .required('Please upload document'),
  requested_at_date: yup.date(),
  recipient_shareholder_type: yup.string().default('exist'),
  recipient_email: yup
    .string()
    .trim()
    .email('Must be a valid email')
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  recipient_name: yup
    .string()
    .trim()
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  recipient_user_first_name: yup
    .string()
    .trim()
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
  recipient_user_last_name: yup
    .string()
    .trim()
    .when('recipient_shareholder_type', {
      is: TransferShareholderType.New,
      then: schema => {
        return schema.required('This field is required');
      },
      otherwise: schema => {
        return schema;
      },
    }),
});

const initialValues = {
  from_shareholder_id: 0,
  to_shareholder_id: 0,
  register_class_id: 0,
  requested_at_date: new Date().toISOString(),
  quantity: 1,
  document_data: [],
  memo: '',
  total: 0,
  register_id: 0,
  price_per_share: 0,
  recipient_shareholder_type: TransferShareholderType.Exist,
  recipient_email: undefined,
  recipient_name: undefined,
  recipient_user_first_name: undefined,
  recipient_user_last_name: undefined,
};

interface RegisterClassSelectPaginate {
  id: number;
  price_per_share: number;
}

export interface IssuerAddTransferModalProps {
  register_id: number;
  open: boolean;
  onClose: () => void;
  shareholderId?: number;
}

export const IssuerAddTransferModal = ({
  register_id,
  open,
  onClose,
  shareholderId,
}: IssuerAddTransferModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      register_id,
      from_shareholder_id: shareholderId ?? initialValues.from_shareholder_id,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const processedValues = {
        ...values,
        ...(values.recipient_shareholder_type === 'exist'
          ? {
              recipient_email: undefined,
              recipient_name: undefined,
              recipient_user_first_name: undefined,
              recipient_user_last_name: undefined,
            }
          : { to_shareholder_id: undefined }),
      };
      await createTransfer(processedValues);
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { data: registerData } = useRegisterQuery(
    formik.values.register_id,
    !formik.values.register_id,
  );

  const { rounding_type, allows_fractional_shares = true } = useMemo(() => {
    const { rounding_type, allows_fractional_shares } = registerData?.data ?? {};
    return { rounding_type, allows_fractional_shares };
  }, [registerData]);

  const { onChangeTotal, onChangeQuantity, onChangePricePerShare } = useQuantityPricePerShareTotal({
    formik,
    allowsFractionalSharesQuantity: allows_fractional_shares,
    roundingTypeQuantity: rounding_type,
  });

  const { mutateAsync: createTransfer, isLoading: isTransferCreating } =
    useIssuerCreateTransferMutation({
      setErrors: formik.setErrors,
    });

  const { data: registersResponse, isLoading: isRegistersLoading } = useCurrentUserRegistersQuery();
  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery(
    { page: 1, page_size: 10, register_id: formik.values.register_id },
    true,
  );

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: formik.values.register_id });

  const registerOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registersResponse?.data) return [];

    return registersResponse.data?.map(item => ({
      label: item.register_obj?.name ?? '',
      value: item.register_id,
    }));
  }, [registersResponse]);

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registerShareholderList?.shareholders) return [];

    return registerShareholderList?.shareholders.map(item => ({
      label: item.entity.entity_name,
      value: item.id,
    }));
  }, [registerShareholderList]);

  const handleAsyncRegisterClassList = async (currentPage: number, search?: string) => {
    const response = await fetchNextRegisterClassListPage({
      pageParam: {
        page: currentPage,
        search,
      },
    });

    const options: SelectPaginateOption<RegisterClassSelectPaginate>[] =
      response.data?.pages.reduce((acc: any, { data, page }) => {
        if (page !== currentPage) {
          return acc;
        }

        return data.map(({ class_name, id, current_price_per_share }) => ({
          label: class_name,
          value: { id, price_per_share: current_price_per_share, toString: () => class_name },
        }));
      }, []);
    return options;
  };

  const handleChangeRegister = (registerId: number | null) => {
    formik.setFieldValue('register_id', registerId);
    formik.setFieldValue('price_per_share', initialValues.price_per_share);
    formik.setFieldValue('to_shareholder_id', 0);
    formik.setFieldValue('from_shareholder_id', 0);

    formik.setFieldError('register_id', '');
    formik.setFieldError('price_per_share', '');
  };

  const handleChangeShareholder = (type: string) => (id: number | null) => {
    if (type === 'recipient') {
      formik.setFieldValue('to_shareholder_id', id);
      formik.setFieldError('to_shareholder_id', '');
    }

    if (type === 'sender') {
      formik.setFieldValue('from_shareholder_id', id);
      formik.setFieldError('from_shareholder_id', '');
    }
  };

  const handleChangeRegisterClass = (register_class_id: number, price_per_share: number) => {
    formik.setFieldValue('register_class_id', register_class_id);
    formik.setFieldValue('price_per_share', price_per_share);
    formik.setFieldValue('quantity', initialValues.quantity);

    setTimeout(() => {
      onChangePricePerShare(price_per_share);
    }, 1);

    formik.setFieldError('register_class_id', '');
    formik.setFieldError('price_per_share', '');
    formik.setFieldError('quantity', '');
    formik.setFieldError('total', '');
  };

  const handleChangeDocuments = useCallback(
    (filesData: string[]) => {
      formik.setFieldValue('document_data', filesData);
      formik.setFieldError('document_data', '');
    },
    [formik],
  );

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    // const currentDate = isSameDay(date?.toDate(), new Date())
    //   ? new Date().toISOString()
    //   : date?.toDate().toISOString();

    const isoStringWithOffset = format(
      new Date(date?.toDate().toISOString()),
      "yyyy-MM-dd'T'HH:mm:ssXXX",
    );
    formik.setFieldError('requested_at_date', '');
    formik.setFieldValue('requested_at_date', isoStringWithOffset);
  };

  const handleCheck = (checked: boolean) => {
    formik.setFieldValue(
      'recipient_shareholder_type',
      checked ? TransferShareholderType.New : TransferShareholderType.Exist,
    );
  };
  const baseURL = import.meta.env.VITE_SR_URL.replace(/\/+$/, '');
  const invitationPath = authPaths.static.invitation.replace(/^\/+/, '');
  const invitationLink = `${baseURL}/${invitationPath}?${APPLY_REGISTER_ID}=${formik.values.register_id}`;

  return (
    <ModalContentWrapper open={open} title={'New transfer'} onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <div className="w-full flex items-start space-x-6">
              <Select
                label="REGISTER"
                options={registerOptions}
                value={formik.values.register_id}
                isLoading={isRegistersLoading}
                onChange={handleChangeRegister}
                error={formik.errors.register_id}
              />
              <SelectPaginate<RegisterClassSelectPaginate>
                key={formik.values.register_id}
                onAsync={handleAsyncRegisterClassList}
                label="CLASS"
                onChange={({ value: { id, price_per_share } }) =>
                  handleChangeRegisterClass(id, price_per_share)
                }
                error={formik.errors.register_class_id}
              />
            </div>
            <div className="w-full flex items-center space-x-6">
              <Select
                key={'sender' + register_id}
                label="sender"
                options={registerShareholderListOptions?.filter(
                  ({ value }) => value !== formik.values.to_shareholder_id,
                )}
                value={formik.values.from_shareholder_id}
                isLoading={isRegisterShareholderListLoading}
                onChange={handleChangeShareholder('sender')}
                error={formik.errors.from_shareholder_id}
              />
              <Select
                key={'recipient' + register_id}
                label="recipient"
                options={registerShareholderListOptions?.filter(
                  ({ value }) => value !== formik.values.from_shareholder_id,
                )}
                value={formik.values.to_shareholder_id}
                isLoading={isRegisterShareholderListLoading}
                onChange={handleChangeShareholder('recipient')}
                error={formik.errors.to_shareholder_id}
                disabled={formik.values.recipient_shareholder_type === TransferShareholderType.New}
              />
            </div>
            <CustomCheckbox
              label="Create new shareholder"
              checked={formik.values.recipient_shareholder_type === TransferShareholderType.New}
              onChange={e => handleCheck(e.target.checked)}
            />
            {formik.values.recipient_shareholder_type === TransferShareholderType.New && (
              <>
                <div
                  style={{ backgroundColor: 'rgba(3, 105, 161, 0.08)' }}
                  className="flex gap-3 py-3 px-4 rounded mt-5 mb-5"
                >
                  <div className="flex items-start -mt-2">
                    <img src={AlertIcon} alt="info" className="w-8 h-8 mt-1" />
                  </div>
                  <div>
                    <p>
                      To add a user who already exists in our system under another register, please
                      follow these steps:
                    </p>
                    <ol style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                      <li>
                        1. Copy the invitation link:{' '}
                        <span
                          className="cursor-pointer hover:underline"
                          title="Click to copy URL"
                          onClick={() =>
                            copyTextToClipBoard(invitationLink, {
                              successMessage: 'Invitation link copied!',
                            })
                          }
                        >
                          {invitationLink}
                        </span>
                      </li>
                      <li>
                        2. Send the copied link to the user you wish to add to the current register.
                      </li>
                    </ol>
                    <p style={{ marginTop: '0.5rem' }}>
                      When the user joins this register, you will be able to see him in the
                      recipients list.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap md:flex-nowrap justify-between items-center space-x-0 md:space-x-6">
                  <TextInputField
                    name="recipient_email"
                    label="Holder email"
                    placeholder="Holder email"
                    disabled={!formik.values.register_class_id}
                  />
                  <div className="mt-5 md:mt-0 w-full">
                    <TextInputField
                      name="recipient_name"
                      label="Holder name"
                      placeholder="Holder name"
                      disabled={!formik.values.register_class_id}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap md:flex-nowrap justify-between items-center space-x-0 md:space-x-6">
                  <TextInputField
                    name="recipient_user_first_name"
                    label="User first name"
                    placeholder="User first name"
                    disabled={!formik.values.register_class_id}
                  />
                  <div className="mt-5 md:mt-0 w-full">
                    <TextInputField
                      name="recipient_user_last_name"
                      label="User last name"
                      placeholder="User last name"
                      disabled={!formik.values.register_class_id}
                    />
                  </div>
                </div>
              </>
            )}

            <CalendarInput
              label="Transfer date"
              name="requested_at_date"
              showTime={true}
              format="DD-MM-YYYY HH:mm"
              onChange={handleChangeDate}
              error={formik.errors.requested_at_date}
              disabledDate={getDisabledDate}
              disabledTime={getDisableTime}
            />
            <CurrencyInput
              currency=""
              name="quantity"
              label="quantity of shares"
              value={formik.values.quantity}
              allowFractional={allows_fractional_shares}
              roundingType={rounding_type}
              onChange={onChangeQuantity}
              disabled={!formik.values.register_class_id}
            />
            <CurrencyInput
              name="price_per_share"
              label={`price per share/unit (${getRegisterTypeStrings('nav', {
                registerId: formik.values.register_id,
              })})`}
              value={formik.values.price_per_share}
              error={formik.errors.price_per_share}
              onChange={onChangePricePerShare}
              disabled={!formik.values.register_class_id}
              decimalPoints={4}
            />

            <CurrencyInput
              name="total"
              label="total"
              value={formik.values.total}
              error={formik.errors.total}
              onChange={onChangeTotal}
              disabled={!formik.values.register_class_id}
            />
            <UploadDocumentsBlock
              label="Upload document JPG, PNG or PDF. Up to 10 mb"
              error={
                Array.isArray(formik.errors.document_data)
                  ? formik.errors.document_data?.[0]
                  : formik.errors.document_data
              }
              onChange={handleChangeDocuments}
              value={formik.values.document_data}
            />
            <TextInputField
              name="memo"
              label="Memo (Optional)"
              placeholder="Memo..."
              textarea
              className="resize-none"
            />
          </div>
          {formik.values.recipient_shareholder_type === TransferShareholderType.New && (
            <div
              style={{ backgroundColor: 'rgba(3, 105, 161, 0.08)' }}
              className="flex gap-3 py-3 px-4 rounded mt-5 mb-5"
            >
              <img src={AlertIcon} alt="info" />
              We will create a user account and an entity for this investor using this email, and
              they will be invited to complete the KYC/KYB process.
            </div>
          )}
          <Button
            onClick={() => formik.handleSubmit()}
            disabled={isTransferCreating || !formik.dirty}
            loading={formik.isSubmitting}
          >
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
